// Signup → "Creating your company" after verification.
//
// One tab claims the setup (users/{uid}.signupClaim) so two tabs (verification
// poll + email-link tab) never create two companies. Before this file the claim
// never expired: if the claiming tab was closed / refreshed / lost its
// connection half-way, every later try (and every log-in) saw the claim, waited
// 15 s and showed "Your account is being set up in another tab" — the company
// was never created.
//
// Now:
//  - a claim older than SIGNUP_CLAIM_STALE_MS (or one released after an error)
//    is taken over — atomically, so still only one tab builds;
//  - the take-over finishes the SAME company (same id, same year + role ids),
//    keeping whatever the earlier try already wrote — no second company, no
//    second financial year, and no rewrite of the company's trial details
//    (the server rules refuse that, which used to block retries too).
// Firebase cost: the same writes as before; a take-over adds 2–3 small reads.

import { doc, getDoc, getDocs, setDoc, updateDoc, runTransaction, collection, query, where, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { initializeChartOfAccounts } from './coaInitializer';

export const SIGNUP_CLAIM_STALE_MS = 90 * 1000;

/**
 * Claim (or take over) the company setup for this user.
 * Returns { action: 'open' | 'wait' | 'create', companyId, fyId?, roleId?, resume? }.
 * `force` = this tab has itself waited past the stale window (guards a wrong clock).
 */
export const claimSignup = (userId, pendingCompanyId, { force = false } = {}) => runTransaction(db, async (tx) => {
  const ref = doc(db, 'users', userId);
  const snap = await tx.get(ref);
  const d = snap.exists() ? snap.data() : {};
  // Already fully created on a previous run → just open it.
  if (Array.isArray(d.companies) && d.companies.length > 0) {
    return { action: 'open', companyId: d.companies[0] };
  }
  const now = new Date().toISOString();
  const prev = d.signupClaim;
  if (prev && prev.companyId) {
    const age = Date.now() - (Date.parse(prev.at) || 0);
    if (!prev.released && age < SIGNUP_CLAIM_STALE_MS && !force) {
      // Another tab claimed it moments ago and is building → wait for it.
      return { action: 'wait', companyId: prev.companyId };
    }
    // The claiming tab stopped (closed / refreshed / offline) or failed → take
    // over and finish the same company.
    const claim = {
      companyId: prev.companyId, at: now, released: false,
      fyId: prev.fyId || null, roleId: prev.roleId || null,
      attempt: (Number(prev.attempt) || 1) + 1,
    };
    tx.set(ref, { signupClaim: claim }, { merge: true });
    return { action: 'create', resume: true, ...claim };
  }
  // Unclaimed → claim it now and become the sole creator. (A claim cleared to
  // null by an older version after an error = a retry → resume the same id.)
  const stamp = Date.now();
  const claim = {
    companyId: pendingCompanyId || `company_${stamp}`, at: now, released: false,
    fyId: `fy_${stamp}`, roleId: `role_admin_${stamp}`, attempt: 1,
  };
  tx.set(ref, { signupClaim: claim }, { merge: true });
  return { action: 'create', resume: prev === null, ...claim };
});

/** After an error: let the next try take over at once (keeps the same ids). */
export const releaseSignupClaim = (userId) =>
  updateDoc(doc(db, 'users', userId), { 'signupClaim.released': true });

/**
 * What an earlier, unfinished try already wrote for this company:
 * { exists, fyId, roleId }. The company is invisible (rules) until it exists,
 * so a refused read simply means "not created yet".
 */
export const findPartialCompany = async (companyId, userId) => {
  let co = null;
  try {
    const s = await getDoc(doc(db, 'companies', companyId));
    if (s.exists()) co = s.data();
  } catch (e) { /* not created yet */ }
  if (!co || co.ownerId !== userId) return { exists: false, fyId: null, roleId: null };
  let roleId = null;
  try {
    const m = await getDoc(doc(db, 'companies', companyId, 'users', userId));
    if (m.exists()) roleId = m.data().roleId || null;
  } catch (e) { /* ignore */ }
  if (!roleId) {
    try {
      const r = await getDocs(query(collection(db, 'companies', companyId, 'roles'), where('isSystemRole', '==', true), limit(1)));
      if (!r.empty) roleId = r.docs[0].id;
    } catch (e) { /* ignore */ }
  }
  return { exists: true, fyId: co.activeFinancialYearId || null, roleId };
};

/**
 * Build the company (same writes as before). `companyDoc` is written only when
 * the company does not exist yet. `onStage(n)` drives the progress steps.
 */
export const buildSignupCompany = async ({ userId, companyId, financialYearId, adminRoleId, companyDoc, writeCompany, data, fyDates, onStage = () => {} }) => {
  // ── Company doc FIRST ─────────────────────────────────────────────────
  // Every write below is gated by the Firestore rule isCompanyOwner (which
  // reads this doc's ownerId), so the company MUST exist before the rest —
  // that's the one ordering constraint. It is therefore NOT parallelised.
  if (writeCompany) await setDoc(doc(db, 'companies', companyId), companyDoc);

  // ── Everything else in PARALLEL ───────────────────────────────────────
  onStage(2);
  const whatsappFull = `${data.waCountryCode} ${data.waNumber}`.trim();
  const parallelResults = await Promise.all([
    // Dashboard balance scoreboard — seeded empty+trusted. NON-FATAL.
    setDoc(
      doc(db, 'companies', companyId, 'summaries', 'balances'),
      { recomputedAt: new Date().toISOString(), seededAtSignup: true },
      { merge: true }
    ).catch((e) => { console.warn('Scoreboard seed skipped:', e?.message); }),
    // Admin role.
    setDoc(doc(db, 'companies', companyId, 'roles', adminRoleId), {
      roleName: 'Admin', description: 'Full access to all modules', isSystemRole: true,
      permissions: {
        dashboard: { view: true }, chartOfAccounts: { view: true, add: true, edit: true, delete: true },
        customers: { view: true, add: true, edit: true, delete: true }, vendors: { view: true, add: true, edit: true, delete: true },
        inventory: { view: true, add: true, edit: true, delete: true },
        purchaseOrder: { view: true, add: true, edit: true, delete: true, approve: true },
        purchaseInvoice: { view: true, add: true, edit: true, delete: true, approve: true },
        saleInvoice: { view: true, add: true, edit: true, delete: true, approve: true },
        receipts: { view: true, add: true, edit: true, delete: true }, payments: { view: true, add: true, edit: true, delete: true },
        reports: { view: true, export: true, viewCost: true, viewBalances: true },
        settings: { view: true, edit: true }, users: { view: true, add: true, edit: true, delete: true }
      },
      createdAt: new Date().toISOString(), createdBy: userId
    }),
    // The owner's company-user membership.
    setDoc(doc(db, 'companies', companyId, 'users', userId), {
      email: data.email, displayName: data.displayName,
      whatsappNumber: whatsappFull,
      role: 'admin', roleId: adminRoleId, isActive: true, permissions: {},
      lastLogin: new Date().toISOString(), createdAt: new Date().toISOString(), createdBy: userId
    }),
    // Active financial year.
    setDoc(doc(db, 'companies', companyId, 'financialYears', financialYearId), {
      yearCode: fyDates.yearCode, yearName: fyDates.yearName,
      startDate: fyDates.startDate, endDate: fyDates.endDate,
      isActive: true, isClosed: false, closedAt: null, closedBy: null, previousYearId: null,
      createdAt: new Date().toISOString(), createdBy: userId
    }),
    // Chart of accounts (its own write-batch; skips when it already exists).
    initializeChartOfAccounts(companyId, userId),
  ]);
  const coaResult = parallelResults[4];
  if (coaResult && !coaResult.success) console.error('Warning: Failed to initialize COA:', coaResult.message);

  // Mark the company fully built. A waiting tab watches this exact field, so it
  // stays the LAST real write — nobody opens a half-built company.
  onStage(3);
  await updateDoc(doc(db, 'users', userId), { companies: [companyId] });
  return whatsappFull;
};
